import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';

// Create styles outside component
const createStyles = (colors, isHoverable) => StyleSheet.create({
  infoItem: {
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.solid_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    gap: 8,
  },
  infoItemHovered: {
    opacity: 0.85,
  },
  infoContent: {
    flex: 1,
    height: '100%',
    justifyContent: 'center',
  },
  infoValue: {
    lineHeight: 16,
  },
  chatIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 'auto',
    opacity: isHoverable ? 1 : 0,
  },
  chatIconHovered: {
    opacity: 1,
  },
  arrowIcon: {
    lineHeight: 15,
    height: 15,
    width: 15,
    marginTop: 0,
    marginLeft: 2,
  },
});

// Memoized text content component
const TextContent = React.memo(({ text, hovered, colors, styles, isClamped }) => (
  <Text 
    color='primary'
    style={[
      styles.infoValue, 
      { textDecorationLine: hovered ? 'underline' : 'none' }
    ]}
    numberOfLines={isClamped ? 2 : undefined}
    accessibilityLabel={text}
  >
    {text}
    <Icon 
      name="arrow-up-right" 
      size={14} 
      color={colors.text_color_primary} 
      style={styles.arrowIcon}
      accessibilityLabel="Opens in new window"
    />
  </Text>
));

TextContent.propTypes = {
  text: PropTypes.string.isRequired,
  hovered: PropTypes.bool.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

TextContent.displayName = 'TextContent';

// Memoized chat icon component
const ChatIcon = React.memo(({ isHoverable, isActive, hovered, colors, styles }) => (
  <View style={[
    styles.chatIcon, 
    !isHoverable && (isActive || hovered) && styles.chatIconHovered
  ]}>
    <Icon 
      name="arrow-up" 
      size={18} 
      color={colors.text_color_primary}
      accessibilityLabel="Start conversation"
    />
  </View>
));

ChatIcon.propTypes = {
  isHoverable: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  hovered: PropTypes.bool.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

ChatIcon.displayName = 'ChatIcon';

const ConversationStarterItem = ({ 
  text, 
  onPress, 
  isHoverable = false, 
  isActive = false,
  isClamped = false,
  style,
}) => {
  const { colors } = useTheme();
  const iconStyle = '2'; // Renamed from style to iconStyle

  // Memoize styles
  const styles = useMemo(() => createStyles(colors, isHoverable), [colors, isHoverable]);

  const handleOnPress = useCallback(() => {
    onPress(text);
  }, [onPress, text]);

  const [hovered, setHovered] = useState(false);

  return (
    <Pressable 
      style={({ hovered: webHovered }) => [
        styles.infoItem,
        (webHovered || hovered) && styles.infoItemHovered,
        style,
      ]}
      onPress={handleOnPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={`Start conversation about: ${text}`}
      accessibilityState={{ selected: isActive }}
    >
      {({ hovered: webHovered }) => {
        const isHovered = webHovered || hovered;
        return (
          <>
            <View style={styles.infoContent}>
              <TextContent
                text={text}
                hovered={isHovered}
                colors={colors}
                styles={styles}
                isClamped={isClamped}
              />
            </View>
            {iconStyle === '1' && (
              <ChatIcon
                isHoverable={isHoverable}
                isActive={isActive}
                hovered={isHovered}
                colors={colors}
                styles={styles}
              />
            )}
          </>
        );
      }}
    </Pressable>
  );
};

ConversationStarterItem.propTypes = {
  text: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
  isHoverable: PropTypes.bool,
  isActive: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

ConversationStarterItem.displayName = 'ConversationStarterItem';

export default React.memo(ConversationStarterItem);